import SEO, { type Crumb } from '@/components/SEO';
import PageHeader from '@/components/PageHeader';
import CTABand from '@/components/CTABand';
import Motif from '@/components/Motif';
import { staticPage } from '@/lib/content';
import { AUTHOR } from '@/lib/site';

// Owner-supplied portrait and selected career references from his WebCenter résumé.

const PROJECT_EXPERIENCE = [
  { name: 'VIA Rail' },
  { name: 'ASML / Cymer' },
  { name: 'Archer Daniels Midland' },
  { name: 'Experian' },
  { name: 'Equifax' },
  { name: 'Metropolitan Transportation Authority' },
  { name: 'U.S. House of Representatives' },
  { name: 'Harvard Pilgrim Healthcare' },
];

const CERTIFICATIONS = [
  'Oracle WebCenter Content and Imaging Certified Implementation Specialist',
  'Oracle UCM/Stellent 10gR3 Content Server Administration',
  'Oracle WebCenter Sites 11g (Architecture; System Administrators)',
  'Oracle Financials Cloud: Payables Implementation Certified Professional',
  'Oracle Cloud Platform Application Integration Certified Specialist',
  'Oracle Cloud Infrastructure Certified Security Professional',
  'Oracle Autonomous Database Cloud Certified Professional',
];

const SECTORS = [
  'Financial services',
  'Healthcare',
  'Federal and state government',
  'Industrial',
  'Transportation',
  'Technology',
];

const HOW = [
  {
    title: 'Specific, or not at all',
    body: 'The exact release, the exact component, the exact patch. If we do not know a thing for certain, we say so and point at the Oracle document that settles it.',
  },
  {
    title: 'The estate comes first',
    body: 'Every recommendation starts from what is running today — versions, customizations, integrations — not from what we would rather be selling.',
  },
  {
    title: 'Written down',
    body: 'Findings, plans and statements arrive as documents you can forward. A conversation is where it starts; a document is where it lands.',
  },
];

const About = () => {
  const page = staticPage('/about');
  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
  ];
  return (
    <>
      <SEO title={page.title} description={page.description} path="/about" breadcrumbs={crumbs} />
      <PageHeader
        crumbs={crumbs}
        label="About"
        title="Oracle WebCenter and ECM specialists."
        lead="Nearly 25 years of WebCenter delivery, with particular depth in invoice recognition, coding, workflows and ERP integration."
      />

      <section aria-labelledby="firm-heading" className="container py-14 md:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
          <div className="max-w-[46rem]">
            <h2 id="firm-heading" className="text-subhead">
              The firm
            </h2>
            <div className="mt-5 space-y-5 text-body">
              <p>
                ECMWorks works on Oracle WebCenter and the processes built around it. That includes the documents
                in the repository, the recognition and coding of invoices, the workflows that route the work,
                and the connections to the ERP. We also support the wider Content, Capture, Portal and Sites estate.
              </p>
              <p>
                Nearly 25 years of delivery brings experience with the parts that cross product boundaries:
                Forms Recognition projects, non-PO account distribution forms, SOA workflows and the platform
                underneath them. An upgrade, an OCI move or a new ERP instance needs those connections understood
                and tested together.
              </p>
              <p>
                The work starts with what the organization needs to change and the business process that must
                continue. That may mean improving a recurring exception, planning a release, migrating content
                or reconnecting invoice processing to E-Business Suite, PeopleSoft, JD Edwards or Fusion Cloud.
              </p>
            </div>

            <h2 className="mt-14 text-subhead">How we work</h2>
            <ul className="mt-6 border-t border-rule">
              {HOW.map((h) => (
                <li key={h.title} className="border-b border-rule py-5">
                  <h3 className="text-small">{h.title}</h3>
                  <p className="mt-1 text-body">{h.body}</p>
                </li>
              ))}
            </ul>
          </div>

          <aside className="min-w-0 lg:pt-2">
            <Motif variant="forest" className="mx-auto hidden w-full max-w-md [&>svg]:h-auto [&>svg]:w-full lg:block" />
            <div className="mt-8 border border-forest bg-cream-light p-7">
              <p className="label">Selected industry experience</p>
              <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
                {SECTORS.map((s) => (
                  <li key={s} className="text-small">
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section aria-labelledby="engineer-heading" className="border-t border-rule bg-cream-light">
        <div className="container py-14 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
            <div className="max-w-[46rem]">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:gap-7">
                <img
                  src="/images/andrew-blackman.webp"
                  srcSet="/images/andrew-blackman-small.webp 320w, /images/andrew-blackman.webp 640w"
                  sizes="(min-width: 640px) 208px, 192px"
                  width="640"
                  height="640"
                  loading="lazy"
                  decoding="async"
                  alt="Andrew Blackman"
                  className="block aspect-square h-auto w-48 max-w-full shrink-0 border border-rule sm:w-52"
                />
                <div>
                  <p className="label">Founder</p>
                  <h2 id="engineer-heading" className="mt-3 text-subhead">
                    {AUTHOR.name}
                  </h2>
                </div>
              </div>
              <div className="mt-5 space-y-5 text-body">
                <p>
                  Andrew has nearly 25 years of WebCenter and enterprise content experience: Content Server administration on UCM and Stellent,
                  Imaging and Capture estates behind accounts payable, Forms Recognition projects, Portal and Sites
                  implementations, and the ERP integrations that connect the documents to the business transaction.
                </p>
                <p>
                  He holds Oracle certifications across the WebCenter family and the Oracle Cloud platform it now runs
                  on — infrastructure, integration, security and database — which informs the assessment of both the platform and the application process.
                </p>
              </div>

              <div className="mt-10 border-t border-rule pt-7">
                <h3 className="text-small">Experience behind ECMWorks</h3>
                <p className="mt-3 text-body">
                  Andrew brings WebCenter delivery experience from projects for organizations including:
                </p>
                <ul className="mt-5 grid grid-cols-1 gap-x-7 sm:grid-cols-2">
                  {PROJECT_EXPERIENCE.map(({ name }) => (
                    <li key={name} className="border-b border-rule py-4">
                      <p className="text-small">{name}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <p className="label">Certifications held</p>
              <ul className="mt-4 border-t border-rule">
                {CERTIFICATIONS.map((c) => (
                  <li key={c} className="border-b border-rule py-3 text-small">
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CTABand variant="engineer" />
    </>
  );
};

export default About;
